import type { Link, Meta } from '@unhead/schema'
import type { UseHeadInput, ActiveHeadEntry, MergeHead } from '@unhead/vue'
import { type RouteLocationNormalizedLoadedGeneric } from 'vue-router'
import type {
  PageProductGiftFragment,
  PageProductStrapFragment,
  PageProductVariationStrapFragment,
  PageProductVariationWatchFragment,
  PageProductWatchFragment,
  ProductVariationStrapTeaserFragment,
  ProductVariationWatchTeaserFragment,
  PageProductVariationGiftFragment,
  PageProductGiveawayFragment,
  PageProductToolFragment,
  PageProductVariationGiveawayFragment,
  PageProductVariationToolFragment,
  MetatagFragment,
  MetatagAttributeFragment,
} from '#graphql-operations'
import type { FieldListItemTyped } from '#blokkli/generated-types'
import type { AnyProductVariationTeaser } from '~/types/product'
import { narrowTypeByProperty } from '~/helpers/type'
import type { UseAppLogger } from '~/server/utils/useAppLogger'

type DrupalRouteMetatags = {
  title: string
  link: Link[]
  meta: Link[]
}

/**
 * Get the page title from the Drupal metatags.
 */
function getTitle(tag: MetatagFragment): string | undefined {
  if (tag.id !== 'title') {
    return
  }

  const value = tag.attributes.find((v) => v.key === 'content')
  if (!value) {
    return
  }

  return value.value
}

/**
 * Build the vue-meta object given the Drupal metatag attributes array.
 *
 * The input is an array of e.g.
 * [{ key: 'one', value: 'foo' }, { key: 'two', value: 'bar'  }]
 *
 * The output is an object where each of the key/value pairs is reduced to a
 * single object:
 * { one: 'foo', two: 'bar' }
 */
function getTagObject(attributes: MetatagAttributeFragment[]): Link | Meta {
  return attributes.reduce<Record<string, string>>((acc, v) => {
    acc[v.key] = v.value
    return acc
  }, {})
}

/**
 * Maps the Drupal metatags to vue-meta compatible definitions.
 */
function getTags(
  metatags: MetatagFragment[] | undefined | null,
): DrupalRouteMetatags {
  try {
    const link: Link[] = []
    const meta: Meta[] = []
    let title: string = ''

    if (metatags) {
      for (let i = 0; i < metatags.length; i++) {
        const tag = metatags[i]
        const tagTitle = getTitle(tag)
        if (tagTitle) {
          title = tagTitle
        } else {
          const item = getTagObject(tag.attributes)
          if (tag.tag === 'link') {
            link.push(item)
          } else if (tag.tag === 'meta') {
            meta.push(item)
          }
        }
      }
    }

    return { link, meta, title }
  } catch (_e) {
    // Noop.
  }
  return { link: [], meta: [], title: '' }
}

/**
 * Type map of Drupal bundle and resulting GraphQL types.
 */
type UseProductRouteTypeMap = {
  watch: {
    product: PageProductWatchFragment
    variationTeaser: ProductVariationWatchTeaserFragment
    variationFull: PageProductVariationWatchFragment
  }
  strap: {
    product: PageProductStrapFragment
    variationTeaser: ProductVariationStrapTeaserFragment
    variationFull: PageProductVariationStrapFragment
  }

  /**
   * Fallback for all other product types.
   */
  type: {
    product:
      | PageProductGiftFragment
      | PageProductGiveawayFragment
      | PageProductToolFragment
    variationTeaser: AnyProductVariationTeaser
    variationFull:
      | PageProductVariationGiftFragment
      | PageProductVariationGiveawayFragment
      | PageProductVariationToolFragment
  }
}

type UseProductRoute<T extends keyof UseProductRouteTypeMap = 'type'> = {
  /**
   * The product entity.
   */
  product: ComputedRef<UseProductRouteTypeMap[T]['product'] | undefined>

  /**
   * All variations of the product.
   *
   * NOTE: For straps, this array is always empty, because there is only
   * a single product for all straps. If we were to load all variations,
   * we would be loading *ALL* strap variations (approx. 8000).
   */
  variations: ComputedRef<Array<UseProductRouteTypeMap[T]['variationTeaser']>>

  /**
   * The current variation. Guaranteed to be available.
   */
  variation: ComputedRef<
    UseProductRouteTypeMap[T]['variationFull'] | undefined | null
  >

  /**
   * The regular content paragraphs of the variation.
   */
  paragraphs: ComputedRef<FieldListItemTyped[]>
}

/**
 * Handles routing for product detail pages.
 *
 * Due to a Bug in Nuxt, when doing a redirect, the page rendering still
 * continues. This is why both the returned product and variation are nullable,
 * because they are in fact null during the duration of the redirect. After
 * the redirect the values are guaranteed to be there, because the composable
 * will throw a 404 error if it isn't.
 *
 * Due to a Bug in Nuxt we need to pass the route object here and cannot use useRoute inside a composable.
 * Similar: https://github.com/nuxt/nuxt/issues/28804
 */
export default async function <T extends keyof UseProductRouteTypeMap = 'type'>(
  bundle: T,
  route: RouteLocationNormalizedLoadedGeneric,
): Promise<UseProductRoute<T>> {
  // eslint-disable-next-line prefer-const -- Can't use const since we only assign it later when we have metatags. But we also need to declare it here since otherwise we get "Cannot access ... before initialization" in on BeforeUnmount() (does not happen in dev mode)
  let headMetatagsEntry: void | ActiveHeadEntry<UseHeadInput<MergeHead>>
  onBeforeUnmount(() => {
    headMetatagsEntry?.dispose()
  })

  let serverLogger: UseAppLogger | undefined
  if (import.meta.server) {
    // Need to use dynamic import here as otherwise we get 'util.inherits is not a function' on the client. https://github.com/winstonjs/winston/issues/2443
    // If we do not import useAppLogger here, the code still works, but throughs an error in the nuxt logs when a PDP is visited.
    const { useAppLogger } = await import('../server/utils/useAppLogger')
    serverLogger = useAppLogger('product-route')
  }

  const slug = computed(() => {
    if (typeof route.params.sku === 'string') {
      return route.params.sku
    }
  })

  if (!slug.value) {
    throw createError({ statusCode: 404, fatal: true })
  }

  const productQueryName = computed(() => {
    if (bundle === 'watch') {
      return 'routeProductVariationWatch'
    } else if (bundle === 'strap') {
      return 'routeProductVariationStrap'
    }

    return 'routeProductVariationType'
  })

  const currentCountry = useCurrentCountry()

  const { data: variation } = await useAsyncData(
    route.path,
    () =>
      useCachedGraphqlQuery(productQueryName.value, {
        slug: slug.value!,
      }).then((v) => narrowTypeByProperty(v.data.variation, 'url')),
    {
      watch: [currentCountry], // needed so country change in Blökkli updates the price in the PDP stage element
    },
  )

  if (!variation.value) {
    serverLogger?.logInfo('Error: no variation for ' + route.fullPath)
    throw createError({ statusCode: 404, fatal: true })
  }

  const url = variation.value.url?.path
  if (!url) {
    serverLogger?.logInfo('Error: no url for variation on ' + route.fullPath)
    throw createError({ statusCode: 404, fatal: true })
  }

  // Our path does not match the path defined by Drupal, so we redirect.
  if (url !== route.path) {
    await navigateTo(url, {
      redirectCode: 301,
      replace: true,
    })
  }

  const product = computed<
    | PageProductGiveawayFragment
    | PageProductGiftFragment
    | PageProductToolFragment
    | PageProductStrapFragment
    | PageProductWatchFragment
    | undefined
  >(() => {
    if (
      variation.value &&
      'product' in variation.value &&
      variation.value.product &&
      'publishedVariationSkus' in variation.value.product
    ) {
      return variation.value?.product
    }
  })

  // The SKUs belonging to the product.
  const skus = computed<{ id: string; sku: string }[]>(
    () => product.value?.publishedVariationSkus || [],
  )

  // All the product variations.
  const variations = computed(() => {
    if (
      product.value &&
      'variations' in product.value &&
      product.value.variations
    ) {
      // if a variation is not published it is returned as null in the variation array.
      return product.value.variations.filter((v) => v !== null)
    }
    return []
  })

  // Handles errors when required data is missing and we don't have a redirect.
  const doError = () => {
    if (!product.value || !skus.value.length || !variation.value) {
      throw createError({ statusCode: 404, fatal: true })
    }
  }

  doError()

  if (process.client) {
    watch(product, doError)
    watch(skus, doError)
  }

  const paragraphs = computed(
    () =>
      (variation.value && 'paragraphs' in variation.value
        ? variation.value.paragraphs || []
        : []) as FieldListItemTyped[],
  )

  const metatags = computed(() => {
    if (variation.value?.metatags?.metatags) {
      return getTags(variation.value.metatags.metatags)
    }

    return {}
  })

  headMetatagsEntry = useHead(metatags)

  return {
    product: product as ComputedRef<UseProductRouteTypeMap[T]['product']>,
    variations,
    variation: computed(() => variation.value), // transform Ref into computedRef
    paragraphs,
  }
}
